import Button from "@cloudscape-design/components/button"
import { useRef, useState } from "react"

interface UploadSourceDocumentButtonProps {
  /**
   * Re-extraction upload handler invoked with the chosen file.
   */
  onUpload: (file: File) => Promise<void>
  /**
   * Cloudscape button variant. Defaults to "normal" (tab-row action); the in-content extraction
   * error state passes "primary" so the remedy reads as the primary next step.
   */
  variant?: "normal" | "primary"
}

/**
 * Button that opens a file picker and triggers re-extraction from a new source document. Reused by
 * the tab-row content actions and the in-content extraction-failed error state so the reupload
 * remedy sits next to the failure it fixes.
 */
const UploadSourceDocumentButton = ({
  onUpload,
  variant = "normal",
}: UploadSourceDocumentButtonProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setUploading(true)
    try {
      await onUpload(file)
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  return (
    <>
      <Button
        variant={variant}
        iconName="upload"
        loading={uploading}
        onClick={() => fileInputRef.current?.click()}
      >
        {uploading ? "Uploading..." : "Reupload Source Document"}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        accept=".xlsx,.xls,.csv,.pdf,.docx"
        onChange={(e) => void handleFileChange(e)}
      />
    </>
  )
}

export default UploadSourceDocumentButton
